// src/services/api/templateAPI.js
import { createAuthHeaders } from '../../utils/authUtils';

const FIREBASE_FUNCTIONS_BASE_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL;

/**
 * Client-side API service for template operations
 */
class TemplateAPI {
  /**
   * Get all templates
   * @param {boolean} activeOnly - Only return active templates (default: true)
   * @returns {Promise<Array>} Array of templates
   */
  static async getAll(activeOnly = true) {
    try {
      console.log('TemplateAPI: Getting all templates', { activeOnly });

      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/templates`);
      url.searchParams.set('operation', 'getAll');
      url.searchParams.set('activeOnly', activeOnly.toString());

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: await createAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'API request was not successful');
      }

      console.log('TemplateAPI: Successfully retrieved templates', {
        count: result.data?.length || 0
      });

      return result.data || [];
    } catch (error) {
      console.error('TemplateAPI Error in getAll:', error);
      throw new Error(`Failed to get templates: ${error.message}`);
    }
  }

  /**
   * Get template by ID
   * @param {string} templateId - Template identifier
   * @returns {Promise<Object|null>} Template data or null
   */
  static async getById(templateId) {
    try {
      console.log('TemplateAPI: Getting template by ID', { templateId });

      if (!templateId) {
        throw new Error('Template ID is required');
      }

      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/templates`);
      url.searchParams.set('operation', 'getById');
      url.searchParams.set('templateId', templateId);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: await createAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'API request was not successful');
      }

      return result.data || null;
    } catch (error) {
      console.error('TemplateAPI Error in getById:', error);
      throw new Error(`Failed to get template: ${error.message}`);
    }
  }

  /**
   * Get templates by trigger type
   * @param {string} triggerType - Trigger type (e.g., 'before_appointment', 'appointment_created')
   * @returns {Promise<Array>} Array of matching templates
   */
  static async getByTriggerType(triggerType) {
    try {
      console.log('TemplateAPI: Getting templates by trigger type', { triggerType });

      if (!triggerType) {
        throw new Error('Trigger type is required');
      }

      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/templates`);
      url.searchParams.set('operation', 'getByTriggerType');
      url.searchParams.set('triggerType', triggerType);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: await createAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'API request was not successful');
      }

      console.log('TemplateAPI: Successfully retrieved templates by trigger', {
        triggerType,
        count: result.data?.length || 0
      });

      return result.data || [];
    } catch (error) {
      console.error('TemplateAPI Error in getByTriggerType:', error);
      throw new Error(`Failed to get templates by trigger: ${error.message}`);
    }
  }

  /**
   * Create a new template
   * @param {Object} templateData - Template data including id
   * @returns {Promise<Object>} Created template
   */
  static async create(templateData) {
    try {
      console.log('TemplateAPI: Creating template', { id: templateData.id });

      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/templates`);
      url.searchParams.set('operation', 'create');

      const headers = await createAuthHeaders();
      headers['Content-Type'] = 'application/json';

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers,
        body: JSON.stringify({ template: templateData }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Create request was not successful');
      }

      console.log('TemplateAPI: Template created', result.data);
      return result.data;
    } catch (error) {
      console.error('TemplateAPI Error in create:', error);
      throw new Error(`Failed to create template: ${error.message}`);
    }
  }

  /**
   * Update an existing template
   * @param {string} templateId - Template ID
   * @param {Object} templateData - Updated template data
   * @returns {Promise<Object>} Updated template
   */
  static async update(templateId, templateData) {
    try {
      console.log('TemplateAPI: Updating template', { templateId });

      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/templates`);
      url.searchParams.set('operation', 'update');
      url.searchParams.set('templateId', templateId);

      const headers = await createAuthHeaders();
      headers['Content-Type'] = 'application/json';

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers,
        body: JSON.stringify({ template: templateData }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Update request was not successful');
      }

      console.log('TemplateAPI: Template updated', result.data);
      return result.data;
    } catch (error) {
      console.error('TemplateAPI Error in update:', error);
      throw new Error(`Failed to update template: ${error.message}`);
    }
  }

  /**
   * Delete a template (soft delete)
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Delete result
   */
  static async delete(templateId) {
    try {
      console.log('TemplateAPI: Deleting template', { templateId });

      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/templates`);
      url.searchParams.set('operation', 'delete');
      url.searchParams.set('templateId', templateId);

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: await createAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Delete request was not successful');
      }

      console.log('TemplateAPI: Template deleted', result.data);
      return result.data;
    } catch (error) {
      console.error('TemplateAPI Error in delete:', error);
      throw new Error(`Failed to delete template: ${error.message}`);
    }
  }

  /**
   * Toggle template active status
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Updated status
   */
  static async toggleActive(templateId) {
    try {
      console.log('TemplateAPI: Toggling template active status', { templateId });

      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/templates`);
      url.searchParams.set('operation', 'toggleActive');
      url.searchParams.set('templateId', templateId);

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: await createAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Toggle request was not successful');
      }

      console.log('TemplateAPI: Template toggled', result.data);
      return result.data;
    } catch (error) {
      console.error('TemplateAPI Error in toggleActive:', error);
      throw new Error(`Failed to toggle template: ${error.message}`);
    }
  }
}

export default TemplateAPI;
